import { useEffect, useRef, type ReactNode } from "react";
import { hasCallbackParams } from "@openmarketsai/app-sdk";
import { consumeUpgradeReturn, handleCallback, setAuthError, setAuthLoading } from "../lib/omauth.js";

/**
 * Completes an OAuth callback on page load — that's the whole job now. The SDK owns all
 * window coordination: in popup mode `signIn()` resolves with the outcome in the opener, and
 * `handleCallback()` self-reports + closes when it runs inside the popup. So both roles are
 * covered by one unconditional call. No popup constants, no postMessage, no window.close.
 * There's no React context — components read auth via the omauth hooks.
 */
export function OmAuthProvider({ children }: { children: ReactNode }) {
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return; // StrictMode runs mount effects twice in dev — do this once.
    ran.current = true;
    // Back from a redirect-mode plan upgrade? (`?om_upgrade=done|canceled`, stripped here.)
    consumeUpgradeReturn();
    if (!hasCallbackParams()) return; // normal load, nothing to complete.
    setAuthLoading(true);
    handleCallback()
      .then((r) => {
        // success stores the session (in omauth); denied/none need no UI; error shows a message.
        if (r.status === "error") setAuthError(r.message);
      })
      .finally(() => setAuthLoading(false));
  }, []);

  return <>{children}</>;
}
